<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { inject, ref } from 'vue'
import AvatarPreview from './game/AvatarPreview.vue'
import AnimatedBackground from '@/components/utils/AnimatedBackground.vue'
import Footer from '@/components/Footer.vue'
import type { RequestRouter } from '@/request/requestRouter'
import { RequestAction } from '@/request/requestRouter'

const { t } = useI18n()

const teamMembers = ref<any>([
	{
		name: 'g0z',
		role: 'about.team.members.g0z.role',
		equipped: [],
		id: import.meta.env.VITE_DOMAIN.includes('dev') ? 2 : 1225,
		description: 'about.team.members.g0z.description',
	},
	{
		name: 'Kryme',
		role: 'about.team.members.kryme.role',
		equipped: [],
		id: 1,
		description: 'about.team.members.kryme.description',
	},
])

const request = inject<RequestRouter>('request')

request?.exec(RequestAction.GetUserProfile, {
	routeParams: {
		userId: teamMembers.value[1].id,
	},
}).then((user) => {
	teamMembers.value[1].equipped = user.player.equipped
	teamMembers.value[1].name = user.username
})

request?.exec(RequestAction.GetUserProfile, {
	routeParams: {
		userId: teamMembers.value[0].id,
	},
}).then((user) => {
	teamMembers.value[0].equipped = user.player.equipped
	teamMembers.value[0].name = user.username
})

function getAvatarFallback(name: string) {
	return name.charAt(0).toUpperCase()
}

function openDiscord() {
	window.open(import.meta.env.VITE_DISCORD_INVITE_LINK, '_blank', 'noopener,noreferrer')
}
</script>

<template>
	<div class="flex w-[100dvw] min-h-[calc(100dvh-50px)] items-center justify-center">
		<AnimatedBackground />

		<!-- Beautiful About Section -->
		<div class="relative z-10 w-full max-w-4xl mx-auto px-6 py-12 mt-[120px]">
			<!-- Header -->
			<div class="text-center mb-12">
				<h2 class="text-2xl md:text-3xl font-semibold text-white mb-4">
					{{ t('about.header.title') }}
				</h2>
				<div class="w-32 h-1 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full" />
			</div>

			<!-- Main Content -->
			<div class="space-y-8">
				<!-- Story Card -->
				<div class="bg-gradient-to-br from-slate-900/80 to-slate-800/80 backdrop-blur-sm rounded-2xl p-8 border-2 border-solid border-slate-700/50 shadow-2xl">
					<div class="space-y-6 text-slate-200 leading-relaxed">
						<p
							class="text-lg"
							v-html="t('about.story.paragraph1', { year: '<span class=\'text-purple-400 font-semibold\'>2023</span>', brandName: '<span class=\'text-pink-400 font-bold\'>FapLab</span>' })"
						/>
						<p v-html="t('about.story.paragraph2', { highlight: `<span class='text-blue-400 font-semibold'>${t('about.story.bestPlatform')}</span>` })" />
						<p v-html="t('about.story.paragraph3', { labName: '<span class=\'text-purple-400 font-semibold\'>Lab</span>' })" />
					</div>
				</div>

				<!-- Vision Card -->
				<div class="bg-gradient-to-br from-purple-900/30 to-pink-900/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-solid border-purple-500/30 shadow-2xl">
					<h3 class="text-2xl font-bold text-white mb-4 flex items-center gap-2">
						<span class="text-purple-400">🚀</span>
						{{ t('about.vision.title') }}
					</h3>
					<div class="space-y-4 text-slate-200">
						<p v-html="t('about.vision.paragraph1', { highlight: `<span class='text-purple-400 font-semibold'>${t('about.vision.packageComplete')}</span>` })" />
						<p v-html="t('about.vision.paragraph2', { highlight: `<span class='text-pink-400 font-semibold'>${t('about.vision.subscriptionSystem')}</span>` })" />
						<p>{{ t('about.vision.paragraph3') }}</p>
					</div>
				</div>

				<!-- Team Section -->
				<div class="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-solid border-blue-500/30 shadow-2xl">
					<h3 class="text-xl sm:text-2xl font-bold text-white mb-6 flex items-center gap-2">
						<span class="text-blue-400 flex-center">👥</span>
						{{ t('about.team.title') }}
					</h3>
					<div class="grid grid-cols-1 md:grid-cols-2 gap-6">
						<router-link
							v-for="member in teamMembers"
							:key="member.name"
							:to="`profile/${member.name}-${member.id}`"
							class="no-underline flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl border-2 border-solid border-slate-600/30"
						>
							<div class="relative flex-shrink-0">
								<AvatarPreview
									v-if="member.equipped"
									:equipped="member.equipped"
									:size="60"
									sub-bg="bg-slate-950"
								/>

								<div
									v-else
									class="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xl ring-2 ring-blue-400/50"
								>
									{{ getAvatarFallback(member.name) }}
								</div>
							</div>
							<div class="flex flex-col gap-1">
								<div class="text-white font-bold text-lg mb-1">
									{{ member.name }}
								</div>
								<span class="text-blue-400 text-sm font-medium">
									{{ t(member.role) }}
								</span>
								<span class="text-slate-300 text-xs mt-1">
									{{ t(member.description) }}
								</span>
							</div>
						</router-link>
					</div>
				</div>

				<!-- Community Card -->
				<div class="bg-gradient-to-br from-indigo-900/30 to-purple-900/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-solid border-indigo-500/30 shadow-2xl">
					<div class="text-center">
						<h3 class="text-2xl font-bold text-white mb-4 flex items-center justify-center gap-2">
							<span class="text-indigo-400">💬</span>
							{{ t('about.community.title') }}
						</h3>
						<p class="text-slate-200 mb-6">
							{{ t('about.community.description') }}
						</p>
						<Button
							class="button-indigo mx-auto"
							icon="pi pi-discord"
							:label="t('daily_tasks.discord.step.0')"
							@click="openDiscord"
						/>
					</div>
				</div>

				<!-- Thank You Card -->
				<div class="bg-gradient-to-br from-pink-900/30 to-red-900/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-solid border-pink-500/30 shadow-2xl text-center">
					<h3 class="text-2xl font-bold text-white mb-4 flex items-center justify-center gap-2">
						<span class="text-pink-400">❤️</span>
						{{ t('about.thanks.title') }}
					</h3>
					<p
						class="text-slate-200 text-lg"
						v-html="t('about.thanks.message', { highlight: `<span class='text-pink-400 font-semibold'>${t('about.thanks.thankYouMessage')}</span>` })"
					/>
				</div>
			</div>
		</div>
	</div>
	<Footer />
</template>

<style scoped lang="scss">
// Animations pour les cartes
.bg-gradient-to-br {
	transition: all 0.3s ease;
}

.bg-gradient-to-br:hover {
	transform: translateY(-2px);
}
</style>
